import { useCallback, useEffect, useRef, useState, type KeyboardEvent } from 'react';
import { NavLink, useLocation } from 'react-router';

import { Icon, Popover } from '@/components/ui';
import {
  menuHoldsPath,
  navEntries,
  type NavEntry,
  type NavLeaf,
} from '@/components/layout/nav-items';
import { useSession } from '@/features/auth/api/auth.api';
import { useClickOutside, useEscapeKey } from '@/hooks';
import { cn } from '@/lib/cn';

type NavMenuEntry = Extract<NavEntry, { kind: 'menu' }>;

/** Tab-shaped: the same face for a plain link and for a menu trigger. */
const ENTRY_CLASS =
  'nav-item flex items-center gap-1.5 rounded-sm px-2.5 py-1.5 text-xs transition-colors';

/**
 * Horizontal navigation, the alternative to the sidebar.
 *
 * It carries the same two-level structure the sidebar does, folded to suit one
 * line: a section becomes a menu, and the groups inside it become headings
 * within that menu. See navEntries, which does the folding.
 *
 * Hidden below `md`, where the off-canvas mobile menu takes over instead -
 * a row of tabs on a phone either wraps into several lines or scrolls out of
 * sight, and neither is worth the vertical space.
 */
export function Navbar() {
  const { data: user } = useSession();
  const { pathname } = useLocation();
  const entries = navEntries(user);

  /* One menu at a time, held here rather than in each menu, so that moving
     along an open bar switches menus the way a desktop menubar does. */
  const [openLabel, setOpenLabel] = useState<string | null>(null);
  const close = useCallback(() => {
    setOpenLabel(null);
  }, []);

  /* A menu left open over the page it just navigated to covers that page.
     Closing on the path rather than on each link also catches re-picking the
     page you are already on. */
  const [lastPath, setLastPath] = useState(pathname);
  if (pathname !== lastPath) {
    setLastPath(pathname);
    setOpenLabel(null);
  }

  if (entries.length === 0) {
    return null;
  }

  return (
    <nav aria-label="Main" className="nav-chrome hidden shrink-0 border-b md:block">
      {/* Left and right walk the bar; each menu handles up and down itself. */}
      <ul className="flex items-center gap-0.5 px-2" onKeyDown={moveAlongBar}>
        {entries.map((entry) =>
          entry.kind === 'link' ? (
            <li key={entry.item.to}>
              <NavbarLink item={entry.item} />
            </li>
          ) : (
            <li key={entry.label}>
              <NavbarMenu
                entry={entry}
                open={openLabel === entry.label}
                onOpen={() => {
                  setOpenLabel(entry.label);
                }}
                onClose={close}
                /* Hovering only takes over a bar that is already open, so
                   passing the pointer across it does not throw menus open. */
                onHover={() => {
                  setOpenLabel((current) => (current === null ? null : entry.label));
                }}
              />
            </li>
          ),
        )}
      </ul>
    </nav>
  );
}

function moveAlongBar(event: KeyboardEvent<HTMLUListElement>): void {
  if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') {
    return;
  }

  const entries = Array.from(event.currentTarget.querySelectorAll<HTMLElement>('[data-nav-entry]'));
  const current = entries.indexOf(event.target as HTMLElement);

  if (current === -1) {
    return;
  }

  event.preventDefault();
  const delta = event.key === 'ArrowRight' ? 1 : -1;
  entries[(current + delta + entries.length) % entries.length]?.focus();
}

/** A destination sitting straight on the bar. */
function NavbarLink({ item }: { item: NavLeaf }) {
  return (
    <NavLink
      to={item.to}
      end={item.end}
      data-nav-entry
      className={({ isActive }) => cn(ENTRY_CLASS, isActive && 'nav-item-active font-bold')}
    >
      <Icon name={item.icon} size={13} />
      <span className="whitespace-nowrap">{item.label}</span>
    </NavLink>
  );
}

/**
 * A section of the navigation, as a dropdown.
 *
 * Menu keyboard semantics are provided in full, as in ActionMenu: opening from
 * the keyboard lands on the first item, up and down move, Escape closes and
 * hands focus back. Opening with the pointer deliberately does not move focus.
 */
function NavbarMenu({
  entry,
  open,
  onOpen,
  onClose,
  onHover,
}: {
  entry: NavMenuEntry;
  open: boolean;
  onOpen: () => void;
  onClose: () => void;
  onHover: () => void;
}) {
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const { pathname } = useLocation();

  const [activeIndex, setActiveIndex] = useState(-1);

  const items = entry.groups.flatMap((group) => group.items);
  /* Items are numbered across the whole menu, headings included, so up and
     down cross a heading rather than stopping at it. */
  const order = new Map(items.map((item, index) => [item.to, index]));
  const holdsCurrent = menuHoldsPath(entry.groups, pathname);

  /**
   * Several groups are laid side by side rather than stacked.
   *
   * Settings holds four of them, and stacked they ran most of the way down
   * the window - a column of seventeen links that has to be scrolled past to
   * reach the last heading. Across, the same seventeen fit in one glance and
   * the headings do the sorting they were added for. A menu with one group
   * stays a plain list, because a single column is not a layout.
   */
  const columns = entry.groups.length > 1;

  /* Where each item sits, so left and right can step between columns. */
  const places = entry.groups.flatMap((group, column) =>
    group.items.map((item, row) => ({ to: item.to, column, row })),
  );

  const dismiss = useCallback(() => {
    onClose();
    setActiveIndex(-1);
  }, [onClose]);

  useClickOutside([triggerRef, panelRef], dismiss, open);
  useEscapeKey(() => {
    dismiss();
    triggerRef.current?.focus();
  }, open);

  useEffect(() => {
    if (!open || activeIndex < 0) {
      return;
    }

    function focusItem(): void {
      panelRef.current?.querySelector<HTMLElement>(`[data-index="${activeIndex}"]`)?.focus();
    }

    focusItem();

    /* Then again on the next turn of the loop. Popover leaves its panel
       `visibility: hidden` until it has measured it, and a hidden element
       refuses focus - so the first attempt lands nowhere whenever measuring
       has not finished, and the menu would open unusable by keyboard. */
    const retry = window.setTimeout(focusItem, 0);

    return () => {
      window.clearTimeout(retry);
    };
  }, [open, activeIndex]);

  /** Up and down, within one column. */
  function move(delta: number): void {
    if (items.length === 0) {
      return;
    }

    setActiveIndex((current) => {
      if (current === -1) {
        return 0;
      }

      /* A flat walk was right while the menu was one column: down crossed a
         heading rather than stopping at it. Laid out across, the same walk
         steps out of the column entirely - down from the last Organisation
         entry landed on the first of People & pay, which is where left and
         right go. Down stays in its column and wraps to the top of it. */
      if (!columns) {
        return (current + delta + items.length) % items.length;
      }

      const here = places[current];
      const group = here ? entry.groups[here.column] : undefined;

      if (!here || !group || group.items.length === 0) {
        return current;
      }

      const row = (here.row + delta + group.items.length) % group.items.length;
      const next = places.findIndex((place) => place.column === here.column && place.row === row);

      return next === -1 ? current : next;
    });
  }

  /** Sideways to the next column, holding the row where the column is long
      enough and landing on its last item where it is not. */
  function moveColumn(delta: number): void {
    const here = places[activeIndex === -1 ? 0 : activeIndex];

    if (!here) {
      return;
    }

    const count = entry.groups.length;
    const column = (here.column + delta + count) % count;
    const target = entry.groups[column];

    if (!target || target.items.length === 0) {
      return;
    }

    const row = Math.min(here.row, target.items.length - 1);
    const next = places.findIndex((place) => place.column === column && place.row === row);

    if (next !== -1) {
      setActiveIndex(next);
    }
  }

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        data-nav-entry
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => {
          if (open) {
            dismiss();
          } else {
            setActiveIndex(-1);
            onOpen();
          }
        }}
        onPointerEnter={onHover}
        onKeyDown={(event) => {
          if (event.key === 'ArrowDown' || event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            setActiveIndex(0);
            onOpen();
          }
        }}
        className={cn(
          ENTRY_CLASS,
          /* The current page is inside, so the trigger stands in for it. */
          holdsCurrent && 'nav-item-active font-bold',
          open && !holdsCurrent && 'nav-item-open',
        )}
      >
        <Icon name={entry.icon} size={13} />
        <span className="whitespace-nowrap">{entry.label}</span>
        <Icon
          name="chevron-down"
          size={11}
          className={cn('transition-transform duration-quick', open && 'rotate-180')}
        />
      </button>

      <Popover
        anchorRef={triggerRef}
        open={open}
        className={cn(
          'max-h-[70vh] py-1',
          columns ? 'max-w-[calc(100vw-1rem)] overflow-auto' : 'min-w-48 overflow-y-auto',
        )}
      >
        <div
          ref={panelRef}
          role="menu"
          aria-label={entry.label}
          /* Equal columns, each as wide as the widest label in the menu, and
             all stretched to the tallest - so the box is a rectangle and the
             rules between the columns run its whole height. */
          className={cn(columns && 'grid items-stretch')}
          style={
            columns
              ? { gridTemplateColumns: `repeat(${String(entry.groups.length)}, minmax(9rem, 1fr))` }
              : undefined
          }
          onKeyDown={(event) => {
            if (event.key === 'ArrowDown') {
              event.preventDefault();
              move(1);
            } else if (event.key === 'ArrowUp') {
              event.preventDefault();
              move(-1);
            } else if (columns && (event.key === 'ArrowRight' || event.key === 'ArrowLeft')) {
              event.preventDefault();
              /* The bar listens for these too, to walk from menu to menu. A
                 portal still bubbles through the React tree, so the open menu
                 has to claim them or sideways would leave it entirely. */
              event.stopPropagation();
              moveColumn(event.key === 'ArrowRight' ? 1 : -1);
            } else if (event.key === 'Home') {
              event.preventDefault();
              setActiveIndex(0);
            } else if (event.key === 'End') {
              event.preventDefault();
              setActiveIndex(items.length - 1);
            }
          }}
        >
          {entry.groups.map((group, index) => (
            <div
              key={group.title ?? 'direct'}
              role="group"
              aria-label={group.title}
              className={cn(
                columns && 'px-1',
                index > 0 &&
                  (columns ? 'border-l border-border' : 'mt-1 border-t border-border pt-1'),
              )}
            >
              {group.title && (
                <p className="px-2.5 py-1 text-2xs font-bold tracking-wide text-content-subtle uppercase">
                  {group.title}
                </p>
              )}

              {group.items.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.end}
                  role="menuitem"
                  tabIndex={-1}
                  data-index={order.get(item.to)}
                  onClick={dismiss}
                  className={({ isActive }) =>
                    cn(
                      'flex items-center gap-2 px-2.5 py-1.5 text-xs outline-none',
                      isActive
                        ? 'bg-primary-soft font-bold text-primary'
                        : 'text-content hover:bg-surface-muted focus:bg-surface-muted',
                    )
                  }
                >
                  <Icon name={item.icon} size={13} className="shrink-0" />
                  <span className="whitespace-nowrap">{item.label}</span>
                </NavLink>
              ))}
            </div>
          ))}
        </div>
      </Popover>
    </>
  );
}
